'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox/combobox';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVehicleMakes } from '@/tenants/_shared/hooks/use-public-lookups';

/** `entrant_vehicles.make` is varchar(100) — don't offer a value the API will reject. */
const MAX_MAKE_LENGTH = 100;

interface VehicleMakeComboboxProps {
  /** Current make value (a catalog make or a custom-typed string). */
  value: string;
  onValueChange: (value: string) => void;
  vehicleType: 'motorcycle' | 'car';
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  popoverClassName?: string;
}

/**
 * Make selector that suggests catalog makes while always allowing an entrant to
 * commit their own make if it isn't listed — the Make-side mirror of
 * {@link VehicleModelCombobox}.
 *
 * Catalog makes come from `useVehicleMakes(type)`. We pass `onSearchChange` to
 * capture what the entrant types — which disables the Combobox's built-in cmdk
 * filtering, so we filter the options ourselves. A "Use \"<typed>\"" create row
 * lets the entrant commit a make that isn't in the catalog: on mobile it leads
 * the list (right under the search box), on desktop it trails the catalog
 * matches so Enter still autocompletes to the best suggestion. Dismissing the
 * sheet/popover with text typed commits that text too (via `onSearchCommit`).
 * The currently-selected custom value is kept in the options so it renders in
 * the trigger. Mobile (drawer) vs desktop (popover) is handled by Combobox.
 *
 * The catalog's "Other" catch-all is dropped from the suggestions here: free
 * entry replaces it, and picking "Other" only produces unusable make data. A
 * previously-saved "Other" still renders, as any custom value does.
 */
export function VehicleMakeCombobox({
  value,
  onValueChange,
  vehicleType,
  disabled,
  placeholder,
  searchPlaceholder,
  className,
  popoverClassName,
}: VehicleMakeComboboxProps) {
  const tAuth = useTranslations('auth');
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');

  const { data: makes = [], isLoading } = useVehicleMakes(vehicleType);

  const makeNames = useMemo(() => makes.filter((make) => make !== 'Other'), [makes]);

  const query = search.trim();
  const lowerQuery = query.toLowerCase();

  const options = useMemo(() => {
    const result: ComboboxOption[] = [];
    const taken = new Set<string>();

    // "Use \"<typed>\"" create row — commit a make that isn't in the catalog.
    const hasExact =
      value.toLowerCase() === lowerQuery || makeNames.some((m) => m.toLowerCase() === lowerQuery);
    const useRow =
      query && !hasExact && query.length <= MAX_MAKE_LENGTH
        ? { value: query, label: tAuth('vehicleMakeUseCustom', { value: query }) }
        : null;

    // Mobile: lead with the create row so it sits right under the search box.
    if (useRow && isMobile) {
      result.push(useRow);
      taken.add(useRow.value);
    }

    // Keep a custom-typed value (not in the catalog) visible & selected in the trigger.
    if (value && !makeNames.includes(value) && !taken.has(value)) {
      result.push({ value, label: value });
      taken.add(value);
    }

    // Catalog suggestions, filtered by the search term (we own filtering because
    // passing onSearchChange disables the Combobox's built-in cmdk filtering).
    for (const name of makeNames) {
      if (taken.has(name)) continue;
      if (query && !name.toLowerCase().includes(lowerQuery)) continue;
      result.push({ value: name, label: name });
      taken.add(name);
    }

    // Desktop: trail with the create row so Enter autocompletes to the best match.
    if (useRow && !isMobile) {
      result.push(useRow);
    }

    return result;
  }, [makeNames, query, lowerQuery, value, isMobile, tAuth]);

  // Dismissed with text typed but nothing tapped — keep what they typed,
  // snapping to the catalog spelling when it matches a suggestion.
  const handleSearchCommit = (typed: string) => {
    if (typed.length > MAX_MAKE_LENGTH) return;
    const catalogMatch = makeNames.find((m) => m.toLowerCase() === typed.toLowerCase());
    onValueChange(catalogMatch ?? typed);
  };

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={onValueChange}
      onSearchChange={setSearch}
      onSearchCommit={handleSearchCommit}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={tAuth('vehicleMakeTypeHint')}
      disabled={disabled || isLoading}
      className={className}
      popoverClassName={popoverClassName}
      enterKeyHint="done"
    />
  );
}
